// resources/js/utils/ChainDetector.js

/**
 * A utility class to detect the specific EVM chain of a contract address
 * by using the native browser `fetch` API to avoid CORS issues.
 */
class ChainDetector {
    constructor() {
        // The list of public RPCs. Ethereum is first to ensure priority.
        this.chains = [
            {
                name: 'ethereum',
                rpcs: [
                    'https://eth.llamarpc.com',
                    'https://rpc.mevblocker.io',
                    'https://1rpc.io/eth',
                ]
            },
            {
                name: 'base',
                rpcs: [
                    'https://base.llamarpc.com',
                    'https://mainnet.base.org',
                    'https://base-rpc.publicnode.com'
                ]
            },
            {
                name: 'polygon',
                rpcs: [
                    'https://polygon-rpc.com',
                    'https://polygon.drpc.org',
                ]
            }
        ];
    }

    /**
     * Asks a public RPC endpoint if it has code for a given address.
     * This method uses the browser's native `fetch` API, which is independent
     * of our global Axios configuration, thereby avoiding all CORS preflight issues.
     */
    async _checkRpc(rpcUrl, address) {
        try {
            const response = await fetch(rpcUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    method: 'eth_getCode',
                    params: [address, 'latest'],
                    id: 1,
                }),
            });

            if (!response.ok) {
                // Handles HTTP errors like 4xx or 5xx
                console.error(`[ChainDetector] RPC ${rpcUrl} returned status ${response.status}`);
                return false;
            }

            const data = await response.json();
            
            // '0x' means no code at this address on this chain. Anything else is a success.
            return data && data.result && data.result !== '0x';

        } catch (error) {
            // Handles network errors (e.g., failed to connect) or JSON parsing errors
            console.error(`[ChainDetector] RPC check FAILED for ${rpcUrl}:`, error.message);
            return false;
        }
    }

    /**
     * Detects the chain of an EVM contract address using a "waterfall" approach.
     */
    async detectEVMChain(address) {
        for (const chain of this.chains) {
            for (const rpc of chain.rpcs) {
                const hasCode = await this._checkRpc(rpc, address);
                if (hasCode) {
                    console.log(`[ChainDetector] SUCCESS: Found ${address} on chain ${chain.name} via ${rpc}`);
                    return chain.name;
                }
            }
        }
        console.warn(`[ChainDetector] Could not find chain for ${address} on any configured network.`);
        return null;
    }
}

// Export a single instance for other modules to use.
export const chainDetector = new ChainDetector();